import { IconInfoCircle } from '@tabler/icons-react';
import { forwardRef, type ReactNode } from 'react';
import styled from 'styled-components';
import { TbIcon } from '../icon/TbIcon.tsx';
import {
  type IPopoverHandle,
  type IPopoverProps,
  Popover,
} from './Popover.tsx';

const InfoPopoverTrigger = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  margin-inline-start: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colorPrimary};
  font: inherit;
  line-height: inherit;
  display: inline-flex;
  align-items: center;
  vertical-align: -0.15em;

  /* Boost dell'icona solo quando il popover è dentro la label di un
     FormItem di Ant (.ant-form-item-label, testo ~14px). Nei contesti
     con font più grande — titoli di Card, header di sezione — l'icona
     resta a 1em (scala con il contesto e non risulta sovradimensionata). */
  .ant-form-item-label & {
    font-size: 1.15em;
    vertical-align: -0.2em;
  }

  &:hover {
    color: ${({ theme }) => theme.colorPrimaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorPrimary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export interface IInfoPopoverProps extends Omit<IPopoverProps, 'children'> {
  /**
   * Icona del trigger. Default: ⓘ (TbInfoCircle).
   */
  icon?: ReactNode;
  /**
   * Etichetta accessibile per lo screen reader sull'icona-trigger.
   * Default: "Mostra informazioni".
   */
  iconAriaLabel?: string;
}

/**
 * Popover informativo con icona ⓘ come trigger (click).
 *
 * Pattern UX 2.3 del design system: per approfondimenti che non devono
 * stare sempre visibili. Usato accanto a label di campi o titoli di sezione,
 * apre un popover con contenuto ricco al click (no hover).
 *
 * Differenza con `tooltip` di Ant Form.Item:
 * - tooltip → hover, contenuto breve (1 riga)
 * - InfoPopover → click, contenuto strutturato (titolo, paragrafi, esempi)
 *
 * Il trigger è un `<button>` HTML nudo (non Ant Button) per non aumentare
 * la baseline del contesto in cui è inserito (es. label di un FormItem).
 * L'icona usa `1em` per scalare con la font-size del label.
 */
export const InfoPopover = forwardRef<IPopoverHandle, IInfoPopoverProps>(
  (props, ref) => {
    const {
      icon = <TbIcon icon={IconInfoCircle} />,
      iconAriaLabel = 'Mostra informazioni',
      ...rest
    } = props;

    return (
      <Popover ref={ref} {...rest}>
        <InfoPopoverTrigger
          type="button"
          aria-label={iconAriaLabel}
          // Il trigger vive dentro contenitori a loro volta cliccabili
          // (<label> di FormItem, header del Collapse nelle ConfigurationCard):
          // click e tastiera non devono propagare.
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          {icon}
        </InfoPopoverTrigger>
      </Popover>
    );
  },
);

InfoPopover.displayName = 'InfoPopover';
